import {MenuItem, SelectProps} from '@material-ui/core';
import React, {ChangeEvent, ReactElement, ReactNode, useCallback, useMemo, useRef} from 'react';
import {Nil} from '../model/SharedTypes';
import {ReactComponent as ChevronIcon} from '../icons/icon-chevron.svg';
import NxCheckbox from '../nxCheckbox/NxCheckbox';
import {createMapping, ValueMapping} from '../nxSelect/ValueMapping';
import styles from './NxMultiSelect.scss';
import NxGenericTextField from '../nxGenericTextField/NxGenericTextField';
import {isNil, isEmpty} from 'lodash/fp';
import {logMissingOnChange} from '../inputError';
import {NxSelectOption, NxSelectProps} from '../nxSelect/NxSelect';

export interface NxMultiSelectProps<T_Value> extends Omit<NxSelectProps<T_Value>, 'value' | 'onChange' | 'onBlur'> {
  value: T_Value[] | null;
  onBlur?: (value: T_Value[]) => void;
  onChange?: (value: T_Value[]) => void;
}

const validValueOrEmptyString = <T_Value,>(value: T_Value[] | null, options: NxSelectOption<T_Value>[]): T_Value[] => {
  if (isNil(value) || isEmpty(value)) {
    return [];
  }

  const optionValues = options.map(option => option.value);
  return value.filter(singleValue => optionValues.includes(singleValue));
}

const calculateValue = <T_Value,>(value: T_Value[] | Nil, lastValue: T_Value[], options: NxSelectOption<T_Value>[]): T_Value[] => {
  if (isNil(value)) {
    return value === null ? lastValue : [];
  }

  return validValueOrEmptyString(value, options);
}

const toValues = <T_Value,>(textValues: string[], valueMapping: ValueMapping<T_Value>): T_Value[] =>
  textValues.map(textValue => {

    const value = valueMapping.toValue(textValue);

    if (value === undefined) {
      throw new Error(`Missing value ${value}`);
    }

    return value;
  });

const fromValues = <T_Value,>(values: T_Value[], valueMapping: ValueMapping<T_Value>): string[] =>
  values.map(value => {

    const textValue = valueMapping.fromValue(value);

    if (textValue === undefined) {
      throw new Error(`Missing text for value ${value}`);
    }

    return textValue;
  });

const selectProps: SelectProps = {
  multiple: true,
  IconComponent: ChevronIcon,
  classes: {
    icon: styles.icon,
    root: styles.select,
    filled: styles.select_filled
  },
  MenuProps: {
    getContentAnchorEl: null,
    classes: {
      list: styles.menu,
      paper: styles.menuWrapper
    },
    anchorOrigin: {
      horizontal: 'left',
      vertical: 'bottom'
    }
  }
};

const itemClasses = {root: styles.item, selected: styles.item_selected};

const NxMultiSelect = <T_Value,>(
    {
      className,
      disabled = false,
      error,
      id,
      label,
      name,
      onBlur,
      onChange,
      options,
      positionAbsoluteError,
      value,
      required
    }: NxMultiSelectProps<T_Value>
  ): ReactElement => {

  const valueMapping = useMemo(() => createMapping(options), [options]);
  const lastValue = useRef(validValueOrEmptyString(value, options));
  const finalValue = calculateValue(value, lastValue.current, options);
  lastValue.current = finalValue;
  const textFinalValue = useMemo(() => fromValues(finalValue, valueMapping), [finalValue, valueMapping])

  const handleChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {

    const textValues = event.target.value as unknown as string[];
    const values = toValues(textValues, valueMapping);

    lastValue.current = values;

    if (!onChange) {
      logMissingOnChange('MultiSelect', name);
      return;
    }

    onChange(values ?? null);
  }, [onChange, name, valueMapping]);

  const handleBlur = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {

    const textValues = event.target.value as unknown as string[];

    if (!onBlur) {
      return;
    }

    onBlur(toValues(textValues, valueMapping) ?? null);
  }, [onBlur, valueMapping]);

  const renderValue = useCallback((selectedTextValues: string[]): string =>
    selectedTextValues.map(textValue => {

      const value = valueMapping.toValue(textValue);

      return options.find(option => option.value === value)?.label;
    }).join(', '),
    [options, valueMapping]);

  const menuItems = useMemo((): ReactNode[] => [
    ...options.map(({disabled, label, value}) => {

      const textValue = valueMapping.fromValue(value);

      return (
        <MenuItem key={textValue} value={textValue} classes={itemClasses} disabled={disabled}>
          <NxCheckbox checked={finalValue.includes(value)} />
          <div className={styles.label}>{label}</div>
        </MenuItem>
      );
    })
  ], [finalValue, options, valueMapping]);

  return (
    <NxGenericTextField disabled={disabled}
                        className={className}
                        error={error}
                        id={id}
                        label={label}
                        name={name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        positionAbsoluteError={positionAbsoluteError}
                        required={required}
                        value={textFinalValue}
                        SelectProps={{...selectProps, renderValue}}
                        select>
      {menuItems}
    </NxGenericTextField>
  );
}

export default NxMultiSelect;
